import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Briefcase, ChevronRight, Search, LayoutGrid, List } from 'lucide-react';
import { useCases } from '../hooks/useCases';
import { Button, Card, EmptyState, IconButton, Input, Skeleton } from '../components/ui';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import type { Case } from '../types';

export default function CaseListPage() {
  const navigate = useNavigate();
  const { data: cases = [], isLoading, error } = useCases();
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>(() => {
    const saved = localStorage.getItem('caseListView');
    return saved === 'list' || saved === 'grid' ? saved : 'grid';
  });

  useDocumentTitle('Cases');

  const changeView = (next: 'grid' | 'list') => {
    setView(next);
    localStorage.setItem('caseListView', next);
  };

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="pb-9 flex flex-col items-center">
          <Skeleton variant="rect" className="h-11 w-44" />
          <Skeleton variant="rect" className="mt-7 h-12 w-full max-w-xl rounded-overlay" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} variant="rect" className="h-20 rounded-overlay" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <p className="text-danger text-lg">Failed to load cases</p>
          <p className="text-ink-soft mt-2">Please check that the server is running.</p>
        </div>
      </div>
    );
  }

  const filtered = cases.filter((c: Case) =>
    c.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Top-right action */}
      <div className="flex justify-end">
        <Button size="lg" icon={Plus} onClick={() => navigate('/cases/new')}>
          New Case
        </Button>
      </div>

      {cases.length === 0 ? (
        <>
          {/* Centered heading */}
          <div className="pb-9 flex flex-col items-center text-center">
            <h1 className="t-display text-green-900">Cases</h1>
          </div>
          <Card padding="none" className="rounded-overlay">
            <EmptyState
              icon={Briefcase}
              title="No cases yet"
              description="Create your first case to get started with property division analysis."
              action={
                <Button size="lg" icon={Plus} onClick={() => navigate('/cases/new')}>
                  Create First Case
                </Button>
              }
            />
          </Card>
        </>
      ) : (
        <>
          {/* Centered hero — heading + search */}
          <div className="pb-9 flex flex-col items-center text-center">
            <h1 className="t-display text-green-900">Cases</h1>

            <div className="w-full max-w-xl mt-7">
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search cases"
                leading={<Search size={20} strokeWidth={1.75} />}
                className="h-12 pl-11 pr-4 rounded-overlay border-border bg-surface-card text-base text-green-900 transition-colors"
              />
            </div>
          </div>

          {/* Controls row */}
          <div className="pb-5 border-b border-border flex items-center justify-between gap-4">
            <span className="text-xs text-ink-faint">
              Showing {filtered.length} {filtered.length === 1 ? 'case' : 'cases'}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-ink-faint hidden sm:block">Updated recently</span>
              <div className="flex items-center p-1 rounded-surface bg-surface-card border border-border">
                <IconButton
                  icon={LayoutGrid}
                  aria-label="Grid view"
                  size="sm"
                  variant="ghost"
                  onClick={() => changeView('grid')}
                  className={
                    view === 'grid'
                      ? 'bg-cream-dark text-green-900 hover:bg-cream-dark'
                      : 'text-ink-faint hover:text-green-900 hover:bg-transparent'
                  }
                />
                <IconButton
                  icon={List}
                  aria-label="List view"
                  size="sm"
                  variant="ghost"
                  onClick={() => changeView('list')}
                  className={
                    view === 'list'
                      ? 'bg-cream-dark text-green-900 hover:bg-cream-dark'
                      : 'text-ink-faint hover:text-green-900 hover:bg-transparent'
                  }
                />
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="mt-10 text-center text-ink-soft">No cases match "{query}".</p>
          ) : (
            <div
              className={`mt-6 grid grid-cols-1 gap-4 ${
                view === 'grid' ? 'sm:grid-cols-2 lg:grid-cols-3' : ''
              }`}
            >
              {filtered.map((c: Case) => {
                const updated = c.updated_at
                  ? `Updated ${new Date(c.updated_at).toLocaleDateString()}`
                  : '';
                return (
                  <Link
                    key={c.id}
                    to={`/cases/${c.public_id}`}
                    className="group text-left bg-surface-card rounded-overlay border border-border hover:shadow-raised hover:border-green-200 hover:-translate-y-px transition-[box-shadow,border-color,transform] p-5 flex items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-medium tracking-tight text-green-900 truncate">
                        {c.name}
                      </h3>
                      <p className="mt-1 text-sm text-ink-faint truncate">{updated}</p>
                    </div>
                    <ChevronRight
                      size={16}
                      className="flex-shrink-0 text-ink-faint group-hover:text-green-600 group-hover:translate-x-0.5 transition-[color,transform]"
                      strokeWidth={2}
                    />
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
